import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { AtSign, CheckCircle2, Clock, Loader2, Lock, Save } from "lucide-react";
import { Link } from "react-router-dom";

// Mittente email personalizzato (alias @giniusapp.it) — configurabile dall'admin titolare.
// Iter122ex · sbloccato per tutti i tenant admin, con upsell banner per i Trial.
export default function EmailSenderCard({ onSaved, embedded = false }) {
  const { user } = useAuth();
  const isTrial = user?.account_type === "trial";
  const [cfg, setCfg] = useState(null);
  const [locale, setLocale] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/tenant/email-sender");
      setCfg(res.data);
      setLocale(res.data.locale || res.data.suggerimento || "");
      setDisplayName(res.data.display_name || "");
    } catch {
      setCfg({ configurabile: false });
    }
  };
  useEffect(() => { load(); }, []);

  const salva = async () => {
    if (isTrial) { toast.error("Funzione disponibile con licenza Light o superiore. Passa a un piano a pagamento per attivare l'alias @giniusapp.it dello studio."); return; }
    const v = (locale || "").trim().toLowerCase();
    if (v.length < 3) { toast.error("Indirizzo troppo corto (min 3 caratteri)"); return; }
    setBusy(true);
    try {
      const res = await api.post("/tenant/email-sender", { locale: v, display_name: displayName });
      setCfg(res.data);
      toast.success(`Mittente salvato: ${res.data.mittente_alias}`);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore nel salvataggio");
    } finally { setBusy(false); }
  };

  if (!cfg) return null;
  if (!cfg.configurabile) {
    if (embedded) return null;
    return (
      <Card className="p-5 border-2" data-testid="email-sender-card">
        <p className="text-sm text-slate-500">{cfg.motivo || "Mittente email gestito dalla piattaforma."}</p>
        {cfg.mittente_effettivo && <p className="text-sm mt-1">Mittente attuale: <b>{cfg.mittente_effettivo}</b></p>}
      </Card>
    );
  }

  const inner = (
    <>
      {isTrial && (
        <div data-testid="email-sender-trial-upsell" className="mb-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <Lock className="w-6 h-6 shrink-0 text-amber-600" />
          <div className="flex-1 text-sm">
            <div className="font-bold text-amber-900">Funzione disponibile con licenza Light o superiore</div>
            <div className="text-amber-800 mt-0.5">Con il piano Trial puoi vedere l&apos;anteprima ma non salvare. Passa a un piano a pagamento per attivare l&apos;alias <b>@{cfg.dominio}</b> dedicato al tuo studio.</div>
          </div>
          <Link to="/piani" data-testid="email-sender-trial-upgrade-btn">
            <Button size="sm" className="h-9 bg-amber-600 hover:bg-amber-700 text-white font-bold whitespace-nowrap">Scopri i piani</Button>
          </Link>
        </div>
      )}
      <p className="text-sm text-slate-500 mb-4">
        Le notifiche email del tuo studio (condòmini, fornitori, collaboratori) partiranno da un indirizzo dedicato
        <b> @{cfg.dominio}</b>. La casella non riceve risposte: chi risponde viene invitato automaticamente a contattare lo studio ai contatti noti.
      </p>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-sm font-semibold text-slate-600">Indirizzo mittente</label>
          <div className="flex items-center mt-1">
            <Input data-testid="email-sender-locale" value={locale} disabled={isTrial}
              onChange={(e) => setLocale(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ""))}
              placeholder="es. studio-rossi" className="h-11 rounded-r-none" />
            <span className="h-11 flex items-center px-3 border border-l-0 rounded-r-md bg-slate-100 text-slate-600 text-sm font-semibold whitespace-nowrap">@{cfg.dominio}</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">Lettere minuscole, numeri, punto e trattino (3-30 caratteri).</p>
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-600">Nome visualizzato</label>
          <Input data-testid="email-sender-display-name" value={displayName} disabled={isTrial}
            onChange={(e) => setDisplayName(e.target.value)} placeholder="es. Studio Rossi" className="h-11 mt-1" />
          <p className="text-xs text-slate-400 mt-1">Comparirà come nome del mittente nelle email.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button data-testid="email-sender-save" disabled={busy || isTrial} onClick={salva} className="h-11 bg-brand hover:bg-brand-dark font-bold">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Salva mittente
        </Button>
        {cfg.configurato && (
          cfg.attivo ? (
            <span data-testid="email-sender-status" className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1.5">
              <CheckCircle2 className="w-4 h-4" /> Attivo · le email partono da {cfg.mittente_alias}
            </span>
          ) : (
            <span data-testid="email-sender-status" className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
              <Clock className="w-4 h-4" /> In attesa verifica dominio · per ora mittente: {cfg.mittente_fallback}
            </span>
          )
        )}
      </div>
    </>
  );

  if (embedded) return <div data-testid="email-sender-card">{inner}</div>;
  return (
    <Card className="p-5 border-2" data-testid="email-sender-card">
      <div className="flex items-center gap-2 mb-2">
        <AtSign className="w-5 h-5 text-brand" />
        <h3 className="font-display text-lg font-bold">Mittente email dello studio</h3>
      </div>
      {inner}
    </Card>
  );
}
